import { CursorMode, SortDirection, type PageMeta } from '@repo/api/web/models'
import type { ColumnSort } from '@tanstack/vue-table'
import { debounce } from 'lodash-es'

type BaseListingQuery<TModel = any> = Partial<{
  Query: string
  Limit: number
  SortField: keyof TModel
  SortDirection: SortDirection
  CursorMode: CursorMode
  CursorField: Array<string>
  CursorId: Array<string>
}>

type BaseListingResponse<TModel> = {
  items?: TModel[]
  meta?: PageMeta
}

type ListingConfig<
  TQuery extends BaseListingQuery = BaseListingQuery,
  TModel extends { id: string | number, [key: string]: any } = any,
> = {
  key: string
  fetcher: (query: TQuery) => Promise<BaseListingResponse<TModel>>
  initialQuery?: Partial<TQuery>
  lazy?: boolean // по умолчанию false тут; ниже управляем immediate
  immediate?: boolean // по умолчанию true
  server?: boolean // по умолчанию true
  transform?: (data: TModel[] | undefined) => TModel[] | undefined
}

export async function useCursorListing<
  TQuery extends BaseListingQuery<TModel>,
  TModel = any,
>(config: ListingConfig<TQuery, TModel>) {
  const pending = ref(false)

  const {
    key,
    fetcher,
    initialQuery = {} as Partial<TQuery>,
    lazy = false,
    immediate = true,
    server = true,
    transform,
  } = config

  const defaultQuery: TQuery = {
    Query: undefined,
    Limit: 10,
    SortField: 'id',
    SortDirection: SortDirection.ASC,
    CursorMode: CursorMode.AFTER,
    CursorField: undefined,
    CursorId: undefined,
  } as unknown as TQuery

  // 1) query в ref, чтобы он прошёл через SSR payload
  const query = ref<TQuery>({
    ...defaultQuery,
    ...initialQuery,
  })

  // 2) основной запрос. Никаких внешних мутаций "state" внутри коллбэка.
  const collection = ref<TModel[]>([])
  const hasBeforeRef = ref<boolean>(true)
  const hasAfterRef = ref<boolean>(true)
  const prevMeta = ref<PageMeta>()

  const load = async (resetCollection = true) => {
    pending.value = true

    try {
      const request = query.value.CursorField
        ? { ...query.value }
        : { ...query.value, CursorMode: undefined }
      const response: BaseListingResponse<TModel> = await fetcher(request)
      const transformed
        = (transform ? (transform(response.items) ?? []) : response.items) ?? []
      if (resetCollection) {
        collection.value = []
      }

      const hasBefore = response.meta?.hasBefore ?? false
      const hasAfter = response.meta?.hasAfter ?? false
      // Для cursor-based пагинации добавляем новые элементы в конец
      switch (query.value.CursorMode) {
        case CursorMode.AFTER:
          collection.value = [...collection.value, ...transformed] as TModel[]
          break
        case CursorMode.BEFORE:
          collection.value = [...transformed, ...collection.value] as TModel[]
          break
      }

      hasBeforeRef.value = hasBefore
      hasAfterRef.value = hasAfter
      prevMeta.value = response.meta

      return {
        data: transformed as TModel[],
        hasBefore,
        hasAfter,
        meta: response.meta,
      }
    } catch (e) {
      hasBeforeRef.value = false
      hasAfterRef.value = false
      throw e
    } finally {
      pending.value = false
    }
  }

  const { data: payload, error } = await useAsyncData(
    `${key}:data`,
    async () => {
      return load()
    },
    {
      // Если immediate=true — не дергаем сразу на клиенте, на сервере выполнится один раз.
      immediate,
      lazy,
      server,
    },
  )
  collection.value = payload.value?.data ?? []
  hasBeforeRef.value = payload.value?.hasBefore ?? false
  hasAfterRef.value = payload.value?.hasAfter ?? false
  // Восстановление меты после SSR-гидрации: load() на клиенте не выполнялся,
  // и без этого первый nextPage() ушёл бы без курсора (CursorField=undefined) —
  // т.е. снова запросил бы первую страницу и продублировал её элементы
  prevMeta.value = payload.value?.meta

  const hasBefore = computed(() => hasBeforeRef.value)
  const hasAfter = computed(() => hasAfterRef.value)
  const transformedCollection = computed(() =>
    transform
      ? (transform(collection.value as TModel[]) ?? [])
      : (collection.value as TModel[]),
  )

  // Методы пагинации (cursor-based)
  function nextPage() {
    const lastItem = collection.value[collection.value.length - 1]
    if (!hasAfter.value || !lastItem) {
      return
    }

    query.value.CursorMode = CursorMode.AFTER
    query.value.CursorField = prevMeta.value?.cursorAfter?.field
    query.value.CursorId = prevMeta.value?.cursorAfter?.id

    return load(false)
  }

  function prevPage() {
    const firstItem = collection.value[0]
    if (!hasBefore.value || !firstItem) {
      return
    }

    query.value.CursorMode = CursorMode.BEFORE
    query.value.CursorField = prevMeta.value?.cursorBefore?.field
    query.value.CursorId = prevMeta.value?.cursorBefore?.id

    return load(false)
  }

  // поиск
  const setSearch = (val: string) => {
    query.value.Query = val
  }
  const searchQuery = computed({
    get: () => query.value.Query || '',
    set: (val: string) => {
      setSearch(val)
      debouncedLoad()
    },
  })
  const debouncedLoad = debounce(() => {
    load(true)
  }, 300)

  // Сброс к начальному состоянию
  const reset = async () => {
    query.value = { ...defaultQuery, ...initialQuery }
    return load(true)
  }

  // Сортировка
  const sort = computed<ColumnSort[]>(() => [
    {
      id: query.value.SortField,
      desc: query.value.SortDirection === SortDirection.DESC,
    },
  ])

  function setSort(sort: ColumnSort[], reload?: boolean) {
    if (!sort.length) {
      query.value.SortField = initialQuery.SortField || defaultQuery.SortField
      query.value.SortDirection
        = initialQuery.SortDirection || defaultQuery.SortDirection
    } else {
      query.value.SortField = sort[0]?.id
      query.value.SortDirection = sort[0]?.desc
        ? SortDirection.DESC
        : SortDirection.ASC
    }

    query.value.CursorField = undefined
    query.value.CursorId = undefined

    if (reload) {
      load(true)
    }
  }

  // Фильтры
  function setFilter(filter: Partial<TQuery>) {
    query.value = { ...query.value, ...filter }
  }

  return {
    query,
    collection: transformedCollection,
    pending,
    error,

    // Пагинация
    hasBefore,
    hasAfter,
    nextPage,
    prevPage,
    /** Мета последнего загруженного батча (курсоры для ссылок пагинации) */
    meta: computed(() => prevMeta.value),

    // Поиск
    searchQuery,
    setSearch,

    // Сортировка
    sort,
    setSort,

    // утилиты
    reset,
    load,

    // Фильтры
    setFilter,
  }
}
